import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "./cn";

/**
 * Spinner — "we are working on it" for a wait short enough that nothing else
 * can usefully be shown (a button mid-submit, a panel fetching one thing).
 *
 * Drawn as a half ring in the CURRENT text colour — so inside a primary button
 * it is the button's text colour, on a page it is the ink. Nothing to theme
 * separately. Half the ring rather than a thin arc: a gap that large is
 * unmistakably turning, where a nearly-closed circle just sits there.
 *
 * (Two sides transparent, not a faint full track — `currentColor` cannot take
 * an opacity modifier, and asking for one silently falls back to Tailwind's
 * own grey, which is off-palette.)
 *
 * Under `prefers-reduced-motion` the site-wide rule in theme.css stops the
 * spin, which is why the accessible label — not the movement — is what
 * actually tells someone a wait is under way.
 */
const spinnerVariants = cva(
  "inline-block animate-spin rounded-full border-current border-b-transparent border-l-transparent align-[-0.125em]",
  {
    variants: {
      size: {
        sm: "h-4 w-4 border-2",
        default: "h-6 w-6 border-[3px]",
        lg: "h-10 w-10 border-4",
      },
    },
    defaultVariants: { size: "default" },
  },
);

export interface SpinnerProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof spinnerVariants> {
  /** Read out instead of shown. @default "Loading" */
  label?: string;
}

export const Spinner = React.forwardRef<HTMLSpanElement, SpinnerProps>(
  ({ className, size, label = "Loading", ...props }, ref) => (
    <span
      ref={ref}
      role="status"
      className={cn("inline-flex shrink-0", className)}
      {...props}
    >
      <span aria-hidden="true" className={cn(spinnerVariants({ size }))} />
      <span className="sr-only">{label}</span>
    </span>
  ),
);
Spinner.displayName = "Spinner";

export { spinnerVariants };
